import sleep from "sleep-promise";

export type CandidateType = "host" | "srflx" | "prflx" | "relay" | "unknown";

export type PeerConnectionTypeResult = {
	local: CandidateType;
	remote: CandidateType;
};

type CandidateObject = {
	candidateType: string;
};

export async function getPeersConnectionType(pc: RTCPeerConnection): Promise<PeerConnectionTypeResult> {
	// wait a little bit as sometimes the stats are not ready straight after the connection is established
	await sleep(100);
	const stats = await pc.getStats();

	let localCandidateType: CandidateType | null = null;
	let remoteCandidateType: CandidateType | null = null;

	for (const { type, state, localCandidateId, remoteCandidateId } of stats.values()) {
		if (type === "candidate-pair" && state === "succeeded") {
			if (localCandidateId) {
				localCandidateType = _getActiveConnectionType(stats, localCandidateId as string);
			}
			if (remoteCandidateId) {
				remoteCandidateType = _getActiveConnectionType(stats, remoteCandidateId as string);
			}
			break;
		}
	}

	// guard: verifies we found the succeeded candidate pair
	if (!localCandidateType || !remoteCandidateType) {
		return {
			local: "unknown",
			remote: "unknown",
		};
	}

	return {
		local: localCandidateType,
		remote: remoteCandidateType,
	};
}

function _getActiveConnectionType(stats: RTCStatsReport, candidateId: string): CandidateType {
	const candidate = (stats.get(candidateId) || null) as CandidateObject | null;

	// guard: verify we got a candidate object. We don't expect a null value, but unknown is returned in case it is so it can be traced back to here from monitoring
	if (!candidate?.candidateType) {
		return "unknown";
	}

	// verify the candidate type is one of the expected types
	if (!["host", "srflx", "prflx", "relay"].includes(candidate.candidateType)) {
		return "unknown";
	}

	return candidate.candidateType as CandidateType;
}
